import { get, toNumber } from 'lodash';

import { DEFAULT_ENVIRONMENT } from 'constants/environments';
import { DEFAULT_SHOP_ID } from 'constants/shops';
import Money from 'utils/Money';

const Settings = class {
  static get _config() {
    return get(window, ['ALLBIRDS', 'shopSettings']);
  }

  static get addToCartPolicyText() {
    return get(Settings._config, 'addToCartPolicyText');
  }

  static get amplitudeApiKey() {
    return get(Settings._config, 'amplitudeApiKey');
  }

  static get amplitudeSessionReplayEnabled() {
    return get(Settings._config, 'amplitudeSessionReplayEnabled', false);
  }

  static get amplitudeSessionReplaySampleRate() {
    return toNumber(get(Settings._config, 'amplitudeSessionReplaySampleRate', 0));
  }

  static get _contentfulSettings() {
    return get(Settings._config, 'contentfulSettings');
  }

  static get contentfulEnvironment() {
    return get(Settings._config, 'contentfulEnvironment');
  }

  static get contentfulSpaceId() {
    return get(Settings._config, 'contentfulSpaceId');
  }

  static get contentfulAccessToken() {
    return get(Settings._config, 'contentfulAccessToken');
  }

  static get shopID() {
    return get(Settings._config, 'shopID', DEFAULT_SHOP_ID);
  }

  static get shopLocale() {
    return get(Settings._config, 'shopLocale');
  }

  static get currentLocale() {
    return get(Settings._config, 'currentLocale');
  }

  static get cartLocale() {
    return get(Settings._config, 'cartLocale');
  }

  static get currency() {
    return get(Settings._config, 'currency');
  }

  static get pdpAcceleratedCheckoutEnabled() {
    return get(Settings._config, 'pdpAcceleratedCheckoutEnabled');
  }

  static get cartAcceleratedPaymentsEnabled() {
    return get(Settings._config, 'cartAcceleratedPaymentsEnabled');
  }

  static get pdpAfterpayEnabled() {
    return get(Settings._config, 'pdpAfterpayEnabled');
  }

  static get finalSaleMessagingEnabled() {
    return get(Settings._config, 'finalSaleMessagingEnabled');
  }

  static get cartUpsellsV2Enabled() {
    return get(Settings._config, 'cartUpsellsV2Enabled');
  }

  static get priceChangeMessagingEnabled() {
    return get(Settings._config, 'priceChangeMessagingEnabled');
  }

  static get digitalGiftCardRecipientForwardingEnabled() {
    return get(Settings._config, 'sendAsAGiftEnabled', false);
  }

  static get giftMessagingEnabled() {
    return get(Settings._config, ['contentfulShopConfiguration', 'showGiftMessaging']);
  }

  static get logoFreePackagingEnabled() {
    return get(Settings._config, ['contentfulShopConfiguration', 'showLogoFreePackaging']);
  }

  static get logoFreeProductsBlocklist() {
    return get(Settings._config, ['contentfulShopConfiguration', 'logoFreeProductsBlocklist']);
  }

  static get enableDiscountPriceColor() {
    return get(Settings._config, ['contentfulShopConfiguration', 'enableDiscountPriceColor']);
  }

  static get freeShippingEnabled() {
    return get(Settings._config, ['contentfulShopConfiguration', 'enableFreeShipping']);
  }

  static get freeShippingThreshold() {
    return get(Settings._config, ['contentfulShopConfiguration', 'freeShippingThreshold']);
  }

  static get cartUpsellsEnabled() {
    return get(Settings._config, 'cartUpsellsEnabled');
  }

  static get cartOverrideCheckoutUrlDomain() {
    return get(Settings._config, 'cartOverrideCheckoutUrlDomain') || false;
  }

  static get globalBanner() {
    const deprecatedGlobalBanner = get(Settings._config, 'globalBanner')
      ? [get(Settings._config, 'globalBanner')]
      : [];

    return get(Settings._config, 'globalBannerV2') || deprecatedGlobalBanner;
  }

  static get globalBannerBackgroundColor() {
    return get(Settings._config, 'globalBannerBackgroundColor');
  }

  static get parentCollectionSortOrder() {
    return get(Settings._config, 'parentCollectionSortOrder');
  }

  static get reviewsEnabled() {
    return !!Settings.yotpoAppKey && get(Settings._config, 'reviewsEnabled');
  }

  static get geofencingEnabled() {
    return get(Settings._config, 'geofencingEnabled');
  }

  static get gladlyBrandId() {
    return get(Settings._config, 'gladlyBrandId');
  }

  static get gladlyAppId() {
    return get(Settings._config, 'gladlyAppId');
  }

  // Unclear why this is used instead of oneTrustScriptID
  static get oneTrustScriptId() {
    return get(Settings._config, 'oneTrustScriptId');
  }

  static get rudderstackAppId() {
    return get(Settings._config, 'rudderstackAppId');
  }

  static get topNavigationData() {
    return get(Settings._contentfulSettings, 'navigation');
  }

  static get breadcrumbNavigationData() {
    return get(Settings._contentfulSettings, 'breadcrumbNavigation');
  }

  static get featureAfterpayPdpEnabled() {
    return get(Settings._contentfulSettings, 'featureEnablePDPAfterpay');
  }

  static get customerAccountEmail() {
    return get(Settings._config, 'customerEmail');
  }

  static get supportedLocales() {
    return get(Settings._config, 'supportedLocales');
  }

  static get shopPermanentDomain() {
    return get(Settings._config, 'shopPermanentDomain');
  }

  static get environment() {
    return get(Settings._config, 'environment', DEFAULT_ENVIRONMENT);
  }

  static get signupModal() {
    return get(Settings._config, 'signupModal') || [];
  }

  static get storefrontAccessToken() {
    return get(Settings._config, 'storefrontAccessToken');
  }

  static get storefrontCartEnabled() {
    return Settings.storefrontAccessToken && get(Settings._config, 'storefrontCartEnabled');
  }

  static get shoprunnerEnabled() {
    return get(Settings._config, 'shoprunnerEnabled');
  }

  static get oneTrustScriptID() {
    return get(Settings._config, 'oneTrustScriptID');
  }

  static get searchIconEnabled() {
    return get(Settings._config, 'searchIconEnabled');
  }

  static get miniNavigation() {
    return get(Settings._contentfulSettings, ['miniNavigation', 'desktop'], {});
  }

  static get isNextGenAnalyticsEnabled() {
    return false;
  }

  static get lowInventoryUrgencyMessageGeosCsv() {
    return get(Settings._config, 'lowInventoryUrgencyMessageGeosCsv');
  }

  static get brandValues() {
    return get(Settings._contentfulSettings, 'brandValues');
  }

  static get brandTenets() {
    return get(Settings._contentfulSettings, 'brandTenets');
  }

  static get storeInventoryEnabled() {
    return get(Settings._contentfulSettings, 'featurePdpStoreInventory', false);
  }

  static get page() {
    return get(window, ['ALLBIRDS', 'page', 'template']);
  }

  static get masterProductCacheEnabled() {
    return get(Settings._config, 'masterProductCacheEnabled', false);
  }

  static get masterProductCacheTTL() {
    return get(Settings._config, 'masterProductCacheTTL');
  }

  static get masterProductCacheVersion() {
    return get(Settings._config, 'masterProductCacheVersion');
  }

  static get globalExperimentPageSections() {
    return get(Settings._contentfulSettings, 'experimentPageSections');
  }

  static get yotpoAppKey() {
    return get(Settings._config, 'yotpoAppKey');
  }

  static get yotpoEnabled() {
    return get(Settings._config, 'yotpoEnabled');
  }

  static get yotpoReviewsInstanceId() {
    return get(Settings._config, 'yotpoReviewsInstanceId');
  }

  static get yotpoStarRatingInstanceId() {
    return get(Settings._config, 'yotpoStarRatingInstanceId');
  }

  static get yotpoTestProductId() {
    return get(Settings._config, 'yotpoTestProductId');
  }

  static get returnsInsuranceConfig() {
    const createVariant = tier => ({
      variantId: get(Settings._config, `returnsInsuranceConfig.variants.${tier}.variantId`),
      value: `$${new Money(
        get(Settings._config, `returnsInsuranceConfig.variants.${tier}.value`),
        'USD'
      ).getDecimalFormat()}`,
    });

    const variants = {
      lowTier: createVariant('lowTier'),
      mediumTier: createVariant('mediumTier'),
      highTier: createVariant('highTier'),
    };
    const variantIds = Object.values(variants)
      .filter(variant => variant.variantId)
      .map(variant => variant.variantId);

    return {
      variants,
      variantIds,
    };
  }
};

export const PAGE_TEMPLATES = {
  HOME: 'index',
  PLP: 'collection.parent',
  CUSTOM_PLP: 'collection.custom',
  PDP: 'product',
};

export default Settings;
